import { i18n } from "@v2/shared/config";
import moment from "moment";
import apiParticipant from "@/api/participant";
import hasCompleteCompany from "@/helpers/event";
import currencies from "@/helpers/constants/currencies";
import store from "@/store";

const state = {
  event: null,
  subParticipants: [],
  companies: [],
  counterparties: [],
  executors: [],
  currentStand: null,
  _overlays: {
    servicesCategories: false
  }
};

const actions = {
  async getEvent({ commit }, id) {
    const response = await apiParticipant.get(id);
    if (!response || !response.data) return;
    commit("SET_EVENT", response.data);
    commit("SET_STAND", {
      stand: response.data?.stands?.length
        ? response.data.stands.find(
            stand => stand.id === +localStorage.getItem(`${id}-stand-id`)
          ) || response.data.stands[0]
        : null,
      refresh: true
    });
  },
  async get({ _, getters, commit, dispatch }, id) {
    await dispatch("getEvent", id);
    if (store.state.event.event.exhibition?.id) {
      await dispatch(
        "fetchAllParticipants",
        store.state.event.event.exhibition.id
      );
    }
    if (
      store.state.event.event?.type === "collective" &&
      !getters.isUserRoleInstaller
    ) {
      await dispatch("fetchSubparticipants", id);
    } else {
      commit("SET_SUB_PARTICIPANTS", []);
    }
  },

  async getCounterparties({ commit }, eventId) {
    const response = await apiParticipant.getCounterparties(eventId);
    if (!response || !response.data) return;
    commit("SET_COUNTERPARTIES", response.data);
  },

  async getExecutors({ commit }, eventId) {
    const response = await apiParticipant.getExecutors(eventId);
    if (!response || !response.data) return;
    commit("SET_EXECUTORS", response.data);
  },

  async fetchAllParticipants({ commit }, exhibitionId) {
    const response = await apiParticipant.getAllParticipants(exhibitionId);
    if (!response || !response.data) return;
    commit("SET_COMPANIES", response.data);
  },

  async fetchSubparticipants({ commit }, eventId) {
    const response = await apiParticipant.getSubParticipants(eventId);
    if (!response || !response.data) return;
    commit("SET_SUB_PARTICIPANTS", response.data);
  },

  addUser({ commit }, user) {
    commit("ADD_USER", user);
  },

  updateUser({ commit }, user) {
    commit("UPDATE_USER", user);
  },

  removeUser({ commit }, userId) {
    commit("REMOVE_USER", userId);
  },

  updateCompany({ commit }, data) {
    commit("SET_COMPANY", data);
  },

  updateCompanyLogo({ commit }, data) {
    commit("SET_COMPANY_LOGO", data);
  },

  setOrderId({ commit }, orderId) {
    commit("SET_ORDER_ID", orderId);
  },

  updateCatalogStatuses({ commit }, status) {
    commit("UPDATE_CATALOG_STATUSES", status);
  },
  async setCurrency({ state, getters, commit, rootGetters }, currency) {
    let participantId = state.event.id;
    const currentUser =
      state.event && state.event.users
        ? state.event.users.find(user => user.id === rootGetters["auth/userId"])
        : null;
    if (getters.isEventSubexponent && !currentUser && state.event?.parent?.id)
      participantId = state.event.parent.id;

    await apiParticipant.setCurrency(participantId, currency);

    commit("SET_CURRENCY", {
      userId: getters.currentUser.id,
      currency,
      currentUser
    });
  },
  setStand({ commit }, stand) {
    commit("SET_STAND", { stand, refresh: false });
  }
};

const mutations = {
  SET_EVENT(state, event) {
    state.event = event;
  },
  UPDATE_CATALOG_STATUSES(state, status) {
    state.event.catalog_order_status = status;
  },
  SET_APPLICATION(state, data) {
    state.event.application = data;
  },

  ADD_USER(state, user) {
    if (!state.event.users) {
      state.event.users = [];
    }

    state.event.users.push(user);
  },

  UPDATE_USER(state, user) {
    state.event.users = state.event.users.map(item => {
      return item.id === user.id ? user : item;
    });
  },

  REMOVE_USER(state, id) {
    const index = state.event.users.findIndex(x => x.id === id);

    if (index > -1) {
      state.event.users.splice(index, 1);
    }
  },

  UPDATE_USER_STATUS(state, { userId, access }) {
    const index = state.event.users.findIndex(x => x.id === userId);

    if (index === -1) {
      return;
    }

    state.event.users[index].access = access;
  },

  SET_COMPANY(state, data) {
    if (!data || !state.event) return;

    state.event.company = data;
  },

  SET_COMPANY_LOGO(state, data) {
    if (!state.event || !state.event.company) return;
    state.event.company.image = data;
  },

  SET_SUB_PARTICIPANTS(state, participants) {
    state.subParticipants = participants;
  },

  SET_ORDER_ID(state, orderId) {
    if (!state.event) return;

    state.event.catalog_order_id = orderId;
  },

  SET_COMPANIES(state, data) {
    state.companies = data;
  },

  SET_COUNTERPARTIES(state, data) {
    state.counterparties = data;
  },

  SET_EXECUTORS(state, data) {
    state.executors = data;
  },

  SET_CURRENCY(state, { userId, currency, currentUser }) {
    if (currentUser) {
      state.event.users = state.event.users.map(item => {
        return item.id === userId ? { ...item, currency: currency } : item;
      });
    } else if (state.event.type === "sub") {
      state.event.parent.users = state.event.parent.users.map(item => {
        return item.id === userId ? { ...item, currency: currency } : item;
      });
    }
  },

  SET_STAND(state, { stand, refresh = false }) {
    if (!stand) return;

    if (
      refresh &&
      state.currentStand &&
      localStorage.getItem(`${state.event.id}-stand-id`) === stand.id &&
      stand.id !== state.currentStand.id
    )
      return;

    state.currentStand = Object.assign({}, stand);
    localStorage.setItem(`${state.event.id}-stand-id`, stand.id);
  }
};

const getters = {
  priceTypes: state => {
    return currencies.filter(item =>
      state.event?.currencies?.includes(item.code)
    );
  },
  isServiceShown(state, getters) {
    return (
      state.event?.exhibition.is_show_order_additional_services &&
      (getters.isEventSelf ||
        getters.isEventOrganizer ||
        getters.isEventSubexponent)
    );
  },
  isEventSelf: state => {
    return state.event && state.event.type === "self";
  },
  isEventOrganizer: state => {
    return state.event && state.event.type === "collective";
  },

  isEventMontage: state => {
    return state.event && state.event.type === "installer";
  },

  isEventSubexponent: state => {
    return state.event && state.event.type === "sub";
  },
  isSpecialProject: state => {
    return state.event && state.event.type === "special-project";
  },
  isPartTime: state => {
    return state.event && state.event.type === "part-time";
  },
  isOrganizerAndMedia: state => {
    return state.event && state.event.type === "organizer-and-media";
  },
  isUserRoleInstaller: (state, getters, rootState, rootGetters) => {
    const userId = rootGetters["auth/userId"];
    return (
      state.event?.users?.find(user => user?.id === userId)?.role ===
      "installer"
    );
  },
  isEventSubexponentViewedByOrganizer: (
    state,
    getters,
    rootState,
    rootGetters
  ) => {
    const userId = rootGetters["auth/userId"];

    return (
      getters.isEventSubexponent &&
      state.event.parent &&
      Array.isArray(state.event.parent.users) &&
      state.event.parent.users.some(user => user.id === userId)
    );
  },

  countSubParticipantsReadAccess: state => {
    return state.subParticipants.filter(participant => !participant.is_edit)
      .length;
  },

  countSubParticipantsEditAccess: state => {
    return state.subParticipants.filter(participant => participant.is_edit)
      .length;
  },

  hasAccessToWrite: (state, getters, rootState, rootGetters) => {
    if (!state.event || !Array.isArray(state.event.users)) return false;

    const userId = rootGetters["auth/userId"];
    let eventUser = state.event.users.find(user => user.id === userId);

    if (eventUser && !state.event.is_edit) return false;

    if (
      !eventUser &&
      state.event.parent &&
      Array.isArray(state.event.parent.users)
    ) {
      eventUser = state.event.parent.users.find(user => user.id === userId);
    }
    return ["supervisor", "admin", "edit"].indexOf(eventUser?.role) !== -1;
  },

  hasOrder: state => {
    return (
      state.event.catalog_order_id &&
      state.event.catalog_order_status !== "draft"
    );
  },

  hasCompleteCompany: state => {
    return state.event ? hasCompleteCompany(state.event.company) : false;
  },

  hasUserAdminRights(state, getters, rootState, rootGetters) {
    if (!state.event || !Array.isArray(state.event.users)) {
      return false;
    }

    const userId = rootGetters["auth/userId"];
    let eventUser = state.event.users.find(u => u.id === userId);

    if (eventUser && !state.event.is_edit) return false;

    if (
      !eventUser &&
      state.event.parent &&
      Array.isArray(state.event.parent.users)
    ) {
      eventUser = state.event.parent.users.find(user => user.id === userId);
    }

    return eventUser && ["supervisor", "admin"].includes(eventUser.role);
  },

  eventId: state => (state.event ? state.event.id : null),

  company: state =>
    state.event && state.event.company ? state.event.company : {},

  companyName: state => {
    if (!state.event || !state.event.company) return "";

    if (i18n.locale === "en" && state.event.company.name_eng) {
      return state.event.company.name_eng;
    }

    if (state.event.company.name_rus) {
      return state.event.company.name_rus;
    }
  },

  companyId: state =>
    state.event && state.event.company ? state.event.company.id : null,

  // Менеджеры выставки
  managers: state => {
    return state.event?.exhibition?.managers?.length
      ? state.event.exhibition.managers
      : [];
  },

  uniquePavilion: state => {
    if (!state.event.stands || !state.event.stands.length) {
      return [];
    }

    const result = [];
    state.event.stands.forEach(stand => {
      if (stand.pavilion && stand.pavilion.parent)
        result.push(stand.pavilion.parent);
    });

    return [...new Map(result.map(item => [item["id"], item])).values()];
  },

  /**
   * Возвращает даты, до которых можно заказывать пропуска.
   */
  passDeadline: (state, getters) => {
    const initialDeadline = {
      certificate: null,
      startCertificate: null,
      startCertificatePrint: null,
      endCertificatePrint: null,
      installation: null,
      startInstallationPrint: null,
      endInstallationPrint: null,
      car: moment(),
      startInstallation: null,
      endDismantling: null
    };

    if (!getters.exhibition) return initialDeadline;

    // Маппинг полей дедлайна на свойства exhibition
    const fieldMappings = {
      certificate: "personal_pass_ended_at",
      startCertificate: "personal_pass_started_at",
      startCertificatePrint: "print_personal_pass_started_at",
      endCertificatePrint: "print_personal_pass_ended_at",
      installation: "installer_pass_ended_at",
      startInstallationPrint: "print_installer_pass_started_at",
      endInstallationPrint: "print_installer_pass_ended_at",
      startInstallation: "installation_started_at", // Возможна опечатка: должно быть startInstallation?
      endDismantling: "dismantling_ended_at"
    };

    const deadline = { ...initialDeadline };

    // Заполняем даты из маппинга
    Object.entries(fieldMappings).forEach(([key, exhibitionKey]) => {
      const dateValue = getters.exhibition[exhibitionKey];
      const m = moment(dateValue);
      deadline[key] = m.isValid() ? m : null;
    });

    // Обработка специального поля car
    const carDate =
      getters.exhibition.dismantling_ended_at || getters.exhibition.ended_at;
    deadline.car = moment(carDate).isValid() ? moment(carDate) : deadline.car;

    return deadline;
  },

  participantType: state => {
    if (!state.event) {
      return "";
    }

    return [
      "installer",
      "self",
      "collective",
      "sub",
      "part-time",
      "organizer-and-media",
      "special-project"
    ].indexOf(state.event.type) !== -1
      ? i18n.t(`event.header.functionalRole.${state.event.type}`)
      : "—";
  },

  eventPavilion: state => {
    if (
      !state.event ||
      !state.event.pavilion_xml_id ||
      !Array.isArray(state.event.pavilions)
    ) {
      return null;
    }

    const pavilionId = state.event.pavilion_xml_id,
      hallName = state.event.hall_rus;
    return state.event.pavilions.find(
      x => x.pavilion_link === pavilionId && x.hall_rus === hallName
    );
  },

  exhibition: state =>
    state.event && state.event.exhibition ? state.event.exhibition : null,

  subParticipantAviableQuotas: state =>
    state.subParticipants.reduce((acc, x) => {
      acc += x.quota;
      return acc;
    }, 0),

  stands: state => state?.event?.stands || [],

  currentUser: (state, getters, rootState, rootGetters) => {
    if (!state.event) return null;

    let currentUser =
      state.event && state.event.users
        ? state.event.users.find(user => user.id === rootGetters["auth/userId"])
        : null;

    if (!currentUser && state.event?.parent?.users?.length) {
      currentUser = state.event.parent.users.find(
        user => user.id === rootGetters["auth/userId"]
      );
    }

    return currentUser;
  },

  eventCurrencies(state) {
    return currencies.filter(item =>
      (state?.event?.currencies || []).includes(item.code)
    );
  },

  currency: (state, getters) => {
    const result = getters.currentUser?.currency
      ? getters.eventCurrencies.find(
          currency => currency.code === getters.currentUser?.currency
        )
      : null;
    return result || getters.eventCurrencies[0];
  },

  /**
   * Фильтрует уведомления выставки по значению в массиве places
   * @returns {Array} - отфильтрованный массив уведомлений
   * @param state
   */
  notificationsByPlace: state => place => {
    if (!state.event?.exhibition?.notifications) {
      return [];
    }
    return state.event.exhibition.notifications.filter(item =>
      item.places?.includes(place)
    );
  }
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
